import { Box, type SxProps } from "@mui/material";
import type { Theme } from "@mui/system";
import type { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { type Department, useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRevealWage } from "../../hooks/useRevealWage";
import { CostDeepDiveUnit, StatsMode } from "../../utils/models";
import { CostDeepDiveChart } from "./CostDeepDive/CostDeepDiveChart";
import {
	useGetAverageAndTotalValuesFromAPIData,
	useGetManagementCenterHoursStats,
	useGetManagementCenterHppdStats,
	useGetManagementCenterPpdStats,
	useGetManagementCenterStaffMix,
	useManagementCenterFilters,
} from "./hooks";
import { ManagementCenterHeader } from "./ManagementCenterHeader";
import { MaximizeButton } from "./MaximizeButton";
import { MaximizedChartDialog } from "./MaximizedChartDialog";
import { ManagementCenterMultiFacility } from "./MultiFacility/ManagementCenterMultiFacility";
import { multiFacilityDepartmentIds } from "./MultiFacility/utils";
import { buildClampRange, MC_PAGE_BG } from "./utils";
import { CallIns } from "./visualizers/CallIns";
import { DisciplinaryOverTime } from "./visualizers/DisciplinaryOverTime";
import { PPDHPPDOverTime } from "./visualizers/PPDHPPDOverTime";
import { ScheduledVsActualCost } from "./visualizers/ScheduledVsActualCost";
import { ShiftPickups } from "./visualizers/ShiftPickups";
import { StaffSatisfaction } from "./visualizers/StaffSatisfaction";
import { Statistics } from "./visualizers/Statistics";

type ChartId = "ppd-hppd" | "cost-deep-dive" | "disciplinary" | "call-ins" | "shift-pickups" | "staff-satisfaction" | "scheduled-vs-actual-cost";

const maximizedContainerSx = { width: "100%", height: "100%" } as const satisfies SxProps<Theme>;

type ManagementCenterProps = {
	embedded?: boolean;
	departmentId?: Department["department_id"];
	sinceDate?: DateTime;
	endDate?: DateTime;
};
export const ManagementCenter: FC<ManagementCenterProps> = ({
	embedded = false,
	departmentId: departmentIdOverride,
	sinceDate: externalSinceDate,
	endDate: externalEndDate,
}) => {
	const { trackError, track } = useAnalytics();
	const { t } = useTranslation();

	const { isAnyDemo, isRealDemo, chosenDepartment, timezone } = useHospitalData();
	const departmentId = departmentIdOverride || chosenDepartment.department_id;

	const canViewWage = useRevealWage();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const {
		shiftHourOptions,
		shiftHoursUpdating,
		shiftHoursUnavailable,
		mcSettingsReady,
		sinceDate: internalSinceDate,
		endDate: internalEndDate,
		onStartDateChange,
		onEndDateChange,
		selectedRoles,
		onRolesChange,
		appliedRoleFilter,
		selectedJobs,
		onJobsChange,
		appliedJobFilter,
		pickupsGroupBy,
		onPickupsGroupByChange,
		mcView,
		onMcViewChange,
		selectedShiftHours,
		onShiftHoursChange,
		shiftHoursForApi,
		isActual: actualDataShown,
		onActualToggleChange,
		laborCostEnabled,
		onLaborCostToggle,
		includeHolidays,
		onIncludeHolidaysToggle,
		excludeHolidaysForApi,
		showAgencyPickups,
		onShowAgencyPickupsToggle,
		excludeAgencyForApi,
		includeLateScheduled,
		onIncludeLateScheduledToggle,
		includeLateScheduledForApi,
		estimateMissingWage,
		onEstimateMissingWageToggle,
		onEstimateMissingWageNoticeDismissed,
		wantedMode,
		onWantedModeToggle,
		wantedModeForApi,
		otMode,
		onOtModeChange,
		onAlignToDayView,
		onSaveMcDefaults,
		onRestoreMcSettings,
	} = useManagementCenterFilters({ trackPrefix: "management_center", departmentIdOverride: departmentId });

	const selectedDateFrame = externalSinceDate ?? internalSinceDate;
	const selectedEndDate = externalEndDate ?? internalEndDate;
	const clampRange = useMemo(() => buildClampRange(selectedDateFrame, selectedEndDate, timezone), [selectedDateFrame, selectedEndDate, timezone]);

	// Multi-facility (portfolio) view: only on the standalone page, and only for users with more than
	// one facility IN THE CHOSEN DEPARTMENT'S NETWORK — the same set the charts compare, so the toggle
	// can't open a view whose queries are all disabled. A stale `?view=multi` deep link (or a
	// cross-network user with one facility here) falls back to single silently.
	const { userInfo } = useAuth();
	const canUseMultiView = !embedded && multiFacilityDepartmentIds(userInfo.departments, chosenDepartment.hospital, chosenDepartment.department_id).length > 1;
	const isMultiView = canUseMultiView && mcView === "multi";

	const [maximizedChart, setMaximizedChart] = useState<ChartId | null>(null);
	// When wage access is revoked, force hours and remember nothing; when restored, default back to cost.
	const [userCostDeepDiveUnit, setUserCostDeepDiveUnit] = useState<CostDeepDiveUnit>(CostDeepDiveUnit.Cost);
	const costDeepDiveUnit: CostDeepDiveUnit = canViewWage ? userCostDeepDiveUnit : CostDeepDiveUnit.Hours;
	const setCostDeepDiveUnit = canViewWage ? setUserCostDeepDiveUnit : undefined;
	// Per-patient-day (PPD/HPPD) toggle for the Cost Deep Dive chart — kept here so it persists between
	// the dashboard card and the maximized dialog (like the unit toggle above).
	const [costDeepDiveByCensus, setCostDeepDiveByCensus] = useState(false);

	const handleMaximize = useCallback(
		(chartId: ChartId) => {
			setMaximizedChart(chartId);
			track("chart_maximize_opened", { chart_id: chartId });
		},
		[track],
	);

	const handleCloseMaximize = useCallback(() => {
		setMaximizedChart(null);
		track("chart_maximize_closed");
	}, [track]);

	const handleCostDeepDiveClick = useCallback(() => {
		track("cost_deep_dive_opened_from_dashboard", { department_id: departmentId });
		navigate(`/${departmentId}/cost-deep-dive?${searchParams.toString()}`);
	}, [departmentId, track, navigate, searchParams]);

	const handleStaffSatisfactionClick = useCallback(() => {
		track("staff_satisfaction_opened_from_dashboard", { department_id: departmentId });
		navigate(`/${departmentId}/staff-satisfaction?${searchParams.toString()}`);
	}, [departmentId, track, navigate, searchParams]);

	// Every query below is parameterized by at least one persisted `mc_*` setting, so all four wait on
	// `mcSettingsReady`. The `|| !mcSettingsReady` on each loading flag keeps the wait inside the
	// existing skeletons — without it a not-yet-enabled query reports `isLoading: false` with no data,
	// which would render an empty chart for the settings round-trip instead of a spinner.
	const {
		data: ppdData,
		isLoading: isLoadingPpdQuery,
		isError: isErrorPpd,
		error: errorPpd,
	} = useGetManagementCenterPpdStats({
		sinceDate: selectedDateFrame,
		endDate: selectedEndDate,
		roles: appliedRoleFilter,
		jobs: appliedJobFilter,
		laborCost: laborCostEnabled,
		shiftHours: shiftHoursForApi,
		excludeHolidays: excludeHolidaysForApi,
		enabled: canViewWage && !isMultiView && mcSettingsReady,
		hideWage: !canViewWage,
		includeLateScheduled: includeLateScheduledForApi,
		wantedMode: wantedModeForApi,
		otMode,
		estimateMissingWage,
		departmentId,
	});
	const {
		data: hppdData,
		isLoading: isLoadingHppdQuery,
		isError: isErrorHppd,
		error: errorHppd,
	} = useGetManagementCenterHppdStats({
		sinceDate: selectedDateFrame,
		endDate: selectedEndDate,
		roles: appliedRoleFilter,
		jobs: appliedJobFilter,
		shiftHours: shiftHoursForApi,
		excludeHolidays: excludeHolidaysForApi,
		includeLateScheduled: includeLateScheduledForApi,
		wantedMode: wantedModeForApi,
		otMode,
		departmentId,
		enabled: !isMultiView && mcSettingsReady,
	});
	const {
		data: hoursData,
		isLoading: isLoadingHoursQuery,
		isError: isErrorHours,
		error: errorHours,
	} = useGetManagementCenterHoursStats({
		sinceDate: selectedDateFrame,
		endDate: selectedEndDate,
		roles: appliedRoleFilter,
		jobs: appliedJobFilter,
		shiftHours: shiftHoursForApi,
		includeLateScheduled: includeLateScheduledForApi,
		wantedMode: wantedModeForApi,
		otMode,
		departmentId,
		enabled: !isMultiView && mcSettingsReady,
	});
	const {
		data: staffMixData,
		isError: isErrorStaffMix,
		error: errorStaffMix,
	} = useGetManagementCenterStaffMix({
		sinceDate: selectedDateFrame,
		endDate: selectedEndDate,
		isActual: actualDataShown,
		roles: appliedRoleFilter,
		jobs: appliedJobFilter,
		laborCost: laborCostEnabled,
		shiftHours: shiftHoursForApi,
		excludeHolidays: excludeHolidaysForApi,
		hideWage: !canViewWage,
		includeLateScheduled: includeLateScheduledForApi,
		otMode,
		estimateMissingWage,
		departmentId,
		enabled: !isMultiView && mcSettingsReady,
	});

	const isLoadingPpd = isLoadingPpdQuery || !mcSettingsReady;
	const isLoadingHppd = isLoadingHppdQuery || !mcSettingsReady;
	const isLoadingHours = isLoadingHoursQuery || !mcSettingsReady;

	useEffect(() => {
		if (isErrorPpd) {
			trackError("fetch_management_center_ppd_stats", {
				error_message: errorPpd?.message,
			});
		}
		if (isErrorHppd) {
			trackError("fetch_management_center_hppd_stats", {
				error_message: errorHppd?.message,
			});
		}
		if (isErrorStaffMix) {
			trackError("fetch_management_center_staff_mix", {
				error_message: errorStaffMix?.message,
			});
		}
		if (isErrorHours) {
			trackError("fetch_management_center_hours_stats", {
				error_message: errorHours?.message,
			});
		}
	}, [isErrorHppd, isErrorPpd, isErrorStaffMix, isErrorHours, errorHppd, errorPpd, errorStaffMix, errorHours, trackError]);

	const avgAndTotalsResult = useGetAverageAndTotalValuesFromAPIData({
		hppdData,
		ppdData,
		staffMixData,
		isActual: actualDataShown,
	});

	useEffect(() => {
		// The multi container tracks its own view event (`view_management_center_multi`).
		if (isMultiView) return;
		track("view_management_center", {
			date_from: selectedDateFrame.toISODate(),
			date_to: selectedEndDate?.toISODate(),
			is_actual_data_shown: actualDataShown,
		});
	}, [track, selectedDateFrame, selectedEndDate, actualDataShown, isMultiView]);

	return (
		<Box
			data-testid="management-center-root"
			sx={{
				width: "100%",
				height: "100%",
				bgcolor: embedded ? "transparent" : MC_PAGE_BG,
				overflow: "auto",
				padding: 2,
				gap: 1,
				display: "flex",
				flexWrap: "wrap",
				justifyContent: "space-between",
				alignContent: "flex-start",
				overflowX: "hidden",
			}}
		>
			<ManagementCenterHeader
				onStartDateChange={onStartDateChange}
				onEndDateChange={onEndDateChange}
				isActual={actualDataShown}
				onActualToggleChange={onActualToggleChange}
				onRolesChange={onRolesChange}
				selectedRoles={selectedRoles}
				selectedJobs={selectedJobs}
				onJobsChange={onJobsChange}
				laborCostEnabled={laborCostEnabled}
				onLaborCostToggle={onLaborCostToggle}
				includeHolidays={includeHolidays}
				onIncludeHolidaysToggle={onIncludeHolidaysToggle}
				includeLateScheduled={includeLateScheduled}
				onIncludeLateScheduledToggle={onIncludeLateScheduledToggle}
				estimateMissingWage={estimateMissingWage}
				onEstimateMissingWageToggle={onEstimateMissingWageToggle}
				onEstimateMissingWageNoticeDismissed={onEstimateMissingWageNoticeDismissed}
				wantedMode={chosenDepartment.statsMode === StatsMode.Ratio ? wantedMode : undefined}
				onWantedModeToggle={chosenDepartment.statsMode === StatsMode.Ratio ? onWantedModeToggle : undefined}
				otMode={otMode}
				onOtModeChange={onOtModeChange}
				onAlignToDayView={onAlignToDayView}
				onSaveMcDefaults={onSaveMcDefaults}
				onRestoreMcSettings={onRestoreMcSettings}
				shiftHourOptions={shiftHourOptions}
				shiftHoursUpdating={shiftHoursUpdating}
				shiftHoursUnavailable={shiftHoursUnavailable}
				selectedShiftHours={selectedShiftHours}
				onShiftHoursChange={onShiftHoursChange}
				hideTimeframe={embedded}
				mode={isMultiView ? "multi" : "single"}
				onModeChange={canUseMultiView ? onMcViewChange : undefined}
			/>
			{isMultiView ? (
				<ManagementCenterMultiFacility
					sinceDate={selectedDateFrame}
					endDate={selectedEndDate}
					isActual={actualDataShown}
					laborCost={laborCostEnabled}
					estimateMissingWage={estimateMissingWage}
					includeLateScheduled={includeLateScheduledForApi}
					otMode={otMode}
					excludeHolidays={excludeHolidaysForApi}
					excludeAgency={excludeAgencyForApi}
					clampRange={clampRange}
				/>
			) : (
				<>
					<Statistics
						gap={0.25}
						data={mcSettingsReady ? avgAndTotalsResult : undefined}
						isActual={actualDataShown}
						laborCostEnabled={laborCostEnabled}
						hideWage={!canViewWage}
						departmentId={departmentId}
					/>
					<PPDHPPDOverTime
						shrinkLegendForSmallScreens={true}
						ppdData={ppdData}
						hppdData={hppdData}
						hoursData={hoursData}
						isLoadingPpd={isLoadingPpd}
						isLoadingHppd={isLoadingHppd}
						isLoadingHours={isLoadingHours}
						hidePpd={!canViewWage}
						trailing={<MaximizeButton onClick={() => handleMaximize("ppd-hppd")} />}
						clampRange={clampRange}
					/>
					<CostDeepDiveChart
						shrinkLegendForSmallScreens={true}
						sinceDate={selectedDateFrame}
						endDate={selectedEndDate}
						isActual={actualDataShown}
						roles={appliedRoleFilter}
						jobs={appliedJobFilter}
						laborCost={laborCostEnabled}
						shiftHours={shiftHoursForApi}
						excludeHolidays={excludeHolidaysForApi}
						includeLateScheduled={includeLateScheduledForApi}
						label={costDeepDiveUnit === CostDeepDiveUnit.Hours ? t("hours") : "Cost"}
						onClick={handleCostDeepDiveClick}
						trailing={<MaximizeButton onClick={() => handleMaximize("cost-deep-dive")} />}
						departmentId={departmentId}
						canToggleUnit={canViewWage}
						unit={costDeepDiveUnit}
						onUnitChange={setCostDeepDiveUnit}
						perCensus={costDeepDiveByCensus}
						onPerCensusChange={setCostDeepDiveByCensus}
						hideWage={!canViewWage}
						otMode={otMode}
						estimateMissingWage={estimateMissingWage}
						ready={mcSettingsReady}
					/>
					{canViewWage && (
						<ScheduledVsActualCost
							shrinkLegendForSmallScreens={true}
							sinceDate={selectedDateFrame}
							endDate={selectedEndDate}
							shiftHoursForApi={shiftHoursForApi}
							appliedRoleFilter={appliedRoleFilter}
							appliedJobFilter={appliedJobFilter}
							laborCostEnabled={laborCostEnabled}
							excludeHolidays={excludeHolidaysForApi}
							includeLateScheduled={includeLateScheduledForApi}
							otMode={otMode}
							estimateMissingWage={estimateMissingWage}
							ready={mcSettingsReady}
							trailing={<MaximizeButton onClick={() => handleMaximize("scheduled-vs-actual-cost")} />}
							departmentId={departmentId}
						/>
					)}
					<DisciplinaryOverTime
						shrinkLegendForSmallScreens={true}
						dateFrom={selectedDateFrame}
						dateTo={selectedEndDate}
						roles={appliedRoleFilter}
						shiftHours={shiftHoursForApi}
						trailing={<MaximizeButton onClick={() => handleMaximize("disciplinary")} />}
						departmentId={departmentId}
					/>
					<CallIns
						shrinkLegendForSmallScreens={true}
						dateFrom={selectedDateFrame}
						dateTo={selectedEndDate}
						roles={appliedRoleFilter}
						jobs={appliedJobFilter}
						shiftHours={shiftHoursForApi}
						excludeHolidays={excludeHolidaysForApi}
						trailing={<MaximizeButton onClick={() => handleMaximize("call-ins")} />}
						departmentId={departmentId}
					/>
					<ShiftPickups
						shrinkLegendForSmallScreens={true}
						dateFrom={selectedDateFrame}
						dateTo={selectedEndDate}
						roles={appliedRoleFilter}
						jobs={appliedJobFilter}
						groupBy={pickupsGroupBy}
						onGroupByChange={onPickupsGroupByChange}
						shiftHours={shiftHoursForApi}
						excludeHolidays={excludeHolidaysForApi}
						excludeAgency={excludeAgencyForApi}
						showAgencyPickups={showAgencyPickups}
						onShowAgencyPickupsToggle={onShowAgencyPickupsToggle}
						trailing={<MaximizeButton onClick={() => handleMaximize("shift-pickups")} />}
						departmentId={departmentId}
					/>
					{(isAnyDemo || isRealDemo) && (
						<StaffSatisfaction
							dateFrom={selectedDateFrame}
							dateTo={selectedEndDate}
							onClick={handleStaffSatisfactionClick}
							trailing={<MaximizeButton onClick={() => handleMaximize("staff-satisfaction")} />}
						/>
					)}
					<MaximizedChartDialog open={maximizedChart !== null} onClose={handleCloseMaximize}>
						{maximizedChart === "ppd-hppd" && (
							<PPDHPPDOverTime
								ppdData={ppdData}
								hppdData={hppdData}
								hoursData={hoursData}
								isLoadingPpd={isLoadingPpd}
								isLoadingHppd={isLoadingHppd}
								isLoadingHours={isLoadingHours}
								hidePpd={!canViewWage}
								containerSx={maximizedContainerSx}
								clampRange={clampRange}
							/>
						)}
						{maximizedChart === "cost-deep-dive" && (
							<CostDeepDiveChart
								label={costDeepDiveUnit === CostDeepDiveUnit.Hours ? t("hours") : "Cost"}
								sinceDate={selectedDateFrame}
								endDate={selectedEndDate}
								isActual={actualDataShown}
								roles={appliedRoleFilter}
								jobs={appliedJobFilter}
								laborCost={laborCostEnabled}
								shiftHours={shiftHoursForApi}
								excludeHolidays={excludeHolidaysForApi}
								includeLateScheduled={includeLateScheduledForApi}
								onClick={handleCostDeepDiveClick}
								containerSx={maximizedContainerSx}
								departmentId={departmentId}
								canToggleUnit={canViewWage}
								unit={costDeepDiveUnit}
								onUnitChange={setCostDeepDiveUnit}
								perCensus={costDeepDiveByCensus}
								onPerCensusChange={setCostDeepDiveByCensus}
								hideWage={!canViewWage}
								otMode={otMode}
								estimateMissingWage={estimateMissingWage}
								ready={mcSettingsReady}
							/>
						)}
						{maximizedChart === "disciplinary" && (
							<DisciplinaryOverTime
								dateFrom={selectedDateFrame}
								dateTo={selectedEndDate}
								roles={appliedRoleFilter}
								shiftHours={shiftHoursForApi}
								containerSx={maximizedContainerSx}
								departmentId={departmentId}
							/>
						)}
						{maximizedChart === "call-ins" && (
							<CallIns
								dateFrom={selectedDateFrame}
								dateTo={selectedEndDate}
								roles={appliedRoleFilter}
								jobs={appliedJobFilter}
								shiftHours={shiftHoursForApi}
								excludeHolidays={excludeHolidaysForApi}
								containerSx={maximizedContainerSx}
								departmentId={departmentId}
							/>
						)}
						{maximizedChart === "shift-pickups" && (
							<ShiftPickups
								dateFrom={selectedDateFrame}
								dateTo={selectedEndDate}
								roles={appliedRoleFilter}
								jobs={appliedJobFilter}
								groupBy={pickupsGroupBy}
								onGroupByChange={onPickupsGroupByChange}
								shiftHours={shiftHoursForApi}
								excludeHolidays={excludeHolidaysForApi}
								excludeAgency={excludeAgencyForApi}
								showAgencyPickups={showAgencyPickups}
								onShowAgencyPickupsToggle={onShowAgencyPickupsToggle}
								containerSx={maximizedContainerSx}
								departmentId={departmentId}
							/>
						)}
						{maximizedChart === "staff-satisfaction" && (
							<StaffSatisfaction dateFrom={selectedDateFrame} dateTo={selectedEndDate} containerSx={maximizedContainerSx} />
						)}
						{maximizedChart === "scheduled-vs-actual-cost" && canViewWage && (
							<ScheduledVsActualCost
								sinceDate={selectedDateFrame}
								endDate={selectedEndDate}
								shiftHoursForApi={shiftHoursForApi}
								appliedRoleFilter={appliedRoleFilter}
								appliedJobFilter={appliedJobFilter}
								laborCostEnabled={laborCostEnabled}
								excludeHolidays={excludeHolidaysForApi}
								includeLateScheduled={includeLateScheduledForApi}
								otMode={otMode}
								estimateMissingWage={estimateMissingWage}
								ready={mcSettingsReady}
								containerSx={maximizedContainerSx}
								departmentId={departmentId}
							/>
						)}
					</MaximizedChartDialog>
				</>
			)}
		</Box>
	);
};
